import type {
  AiHandoffFigure,
  AiHandoffRow,
  AiHandoffSection,
  AiHandoffSectionId,
  EstimateSectionId,
} from '@popety-io/types'
import {
  SECTION_SPECS,
  type SectionField,
  type SectionSpec,
} from './sectionFields'

const isRow = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

/**
 * Reads a dot path, returning undefined rather than throwing on any gap.
 * A numeric segment indexes an array, so `lands.0.area` works.
 */
const splitPath = (path: string): string[] => {
  // `a.b.[50.0].c` — brackets quote a key that itself contains dots, which
  // Elasticsearch percentile keys do.
  const parts: string[] = []
  for (const raw of path.split(/\.(?![^[]*\])/)) {
    parts.push(
      raw.startsWith('[') && raw.endsWith(']') ? raw.slice(1, -1) : raw
    )
  }
  return parts
}

const at = (root: unknown, path: string): unknown => {
  // An empty path means the value itself — the saved-listing sections store a
  // bare array at the section root rather than under a named key.
  if (path === '') return root
  let cur: unknown = root
  for (const part of splitPath(path)) {
    if (Array.isArray(cur)) {
      const i = Number(part)
      if (!Number.isInteger(i)) return undefined
      // Negative indexes count from the end, so `trends.-1` reads the most
      // recent year of a series stored oldest-first.
      cur = i < 0 ? cur[cur.length + i] : cur[i]
      continue
    }
    if (!isRow(cur)) return undefined
    cur = cur[part]
  }
  return cur
}

/**
 * Turns one declared field into an emitted value. A declared unit means the
 * value must be a finite number to be emitted at all — a unit attached to a
 * string would be a lie, and a number without its declared unit is the very
 * ambiguity this payload removes, so neither is emitted.
 */
const valueFor = (
  raw: unknown,
  unit: AiHandoffFigure['unit'] | undefined,
  source: AiHandoffFigure['source'],
  asOf: string,
  transform?: (v: number) => number | null
): AiHandoffFigure | string | number | boolean | null | undefined => {
  if (raw === null || raw === undefined) return undefined
  if (unit !== undefined) {
    if (typeof raw !== 'number' || !Number.isFinite(raw)) return undefined
    const value = transform ? transform(raw) : raw
    // `null` from a transform is deliberate: the source carried a sentinel
    // (-1 for an unknown area, 0 for a price on request). Emitting the figure
    // with a null value says "unknown", which is information; dropping the
    // field entirely would leave a reader to guess it was never collected.
    if (value === null) return { value: null, unit, source, as_of: asOf }
    return Number.isFinite(value)
      ? { value, unit, source, as_of: asOf }
      : undefined
  }
  if (typeof raw === 'string') return raw === '' ? undefined : raw
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : undefined
  if (typeof raw === 'boolean') return raw
  return undefined
}

/**
 * A list of primitive strings, e.g. a listing's image URLs. Non-strings are
 * dropped rather than coerced, and an empty result is omitted entirely.
 */
const listValue = (raw: unknown, listPath?: string): string[] | undefined => {
  if (!Array.isArray(raw)) return undefined
  const items = listPath ? raw.map((r) => at(r, listPath)) : raw
  const out = items.filter(
    (v): v is string => typeof v === 'string' && v.length > 0
  )
  return out.length > 0 ? out : undefined
}

/**
 * The unit for one field on one row. Normally static, but a series whose rows
 * mix quantities resolves it from a sibling field — see `unit_by`.
 */
const unitFor = (
  item: unknown,
  f: SectionField
): AiHandoffFigure['unit'] | undefined => {
  if (!f.unit_by) return f.unit
  const key = at(item, f.unit_by.path)
  if (typeof key !== 'string') return f.unit
  return f.unit_by.map[key] ?? f.unit
}

/** Resolves one declared field against a value, honouring `kind: 'list'`. */
const fieldValue = (
  item: unknown,
  f: SectionField,
  asOf: string
): ReturnType<typeof valueFor> | string[] => {
  if (f.kind === 'list') return listValue(at(item, f.path), f.list_path)
  const stored = at(item, f.path)
  const v = valueFor(
    f.derive ? f.derive(stored) : stored,
    unitFor(item, f),
    f.source,
    asOf,
    f.transform
  )
  return typeof v === 'string' && f.max_chars ? v.slice(0, f.max_chars) : v
}

/** Newest first by `sort_desc_by`, then at most `limit` rows. Only for arrays. */
const orderedRows = (raw: unknown[], field: SectionField): unknown[] => {
  let rows = raw
  if (field.sort_desc_by) {
    const key = field.sort_desc_by
    rows = rows
      .slice()
      .sort((a, b) =>
        String(at(b, key) ?? '').localeCompare(String(at(a, key) ?? ''))
      )
  }
  return field.limit ? rows.slice(0, field.limit) : rows
}

/**
 * Builds a complete series. Arrays map row-for-row; an object keyed by period
 * (market quarters like "2006-Q1") becomes rows carrying that key. Series are
 * emitted whole rather than sampled — a model asked to chart price evolution
 * cannot do it from a summary, which is why an earlier version of this file
 * summarising 82 quarters into six numbers was wrong.
 */
const seriesRows = (
  raw: unknown,
  field: SectionField,
  asOf: string
): AiHandoffRow[] => {
  const items = field.item ?? []
  const entries: Array<[string | null, unknown]> = Array.isArray(raw)
    ? orderedRows(raw, field).map((r) => [null, r])
    : isRow(raw)
      ? Object.entries(raw)
      : []

  const rows: AiHandoffRow[] = []
  const readInto = (row: AiHandoffRow, item: unknown): void => {
    for (const f of items) {
      const v = fieldValue(item, f, asOf)
      if (v !== undefined) row[f.key] = v
    }
  }

  for (const [key, item] of entries) {
    // An outer key can come from the object key (market quarters) or from a
    // bucket's own `key` property (Elasticsearch terms aggregations).
    const outerKey = field.period_path
      ? at(item, field.period_path)
      : (key ?? (isRow(item) ? item.key : undefined))

    if (field.explode) {
      const inner = at(item, field.explode)
      if (!Array.isArray(inner)) continue
      for (const sub of inner) {
        const row: AiHandoffRow = {}
        if (
          field.period_key &&
          (typeof outerKey === 'string' || typeof outerKey === 'number')
        ) {
          row[field.period_key] = outerKey
        }
        if (field.explode_key && isRow(sub)) {
          const k = sub.key
          if (
            field.explode_key_min !== undefined &&
            typeof k === 'number' &&
            k < field.explode_key_min
          ) {
            continue
          }
          if (typeof k === 'string' || typeof k === 'number') {
            row[field.explode_key] = k
          }
        }
        readInto(row, sub)
        if (Object.keys(row).length > 0) rows.push(row)
      }
      continue
    }

    const row: AiHandoffRow = {}
    if (key !== null && field.period_key) row[field.period_key] = key
    readInto(row, item)
    if (Object.keys(row).length > 0) rows.push(row)
  }
  return rows
}

const QUARTER = /^(\d{4})-Q([1-4])$/

const figureValue = (v: unknown): AiHandoffFigure | null =>
  isRow(v) && typeof v.value === 'number' && typeof v.unit === 'string'
    ? (v as unknown as AiHandoffFigure)
    : null

/**
 * The last four quarters of a market series against the same four quarters one
 * and five years earlier. A window with a missing quarter is left out rather
 * than compared on fewer quarters.
 */
const trendRows = (
  rows: AiHandoffRow[],
  field: SectionField,
  asOf: string
): AiHandoffRow[] => {
  const trend = field.trend
  const periodKey = field.period_key
  if (!trend || !periodKey) return []

  const byQuarter = new Map<
    number,
    { value: AiHandoffFigure; weight: number }
  >()
  for (const row of rows) {
    const m = QUARTER.exec(String(row[periodKey] ?? ''))
    const value = figureValue(row[trend.value_key])
    const weight = figureValue(row[trend.weight_key])?.value ?? 0
    if (m && value && weight > 0) {
      byQuarter.set(Number(m[1]) * 4 + Number(m[2]) - 1, { value, weight })
    }
  }
  if (byQuarter.size === 0) return []

  const label = (q: number) => `${Math.floor(q / 4)}-Q${(q % 4) + 1}`
  const last = Math.max(...byQuarter.keys())
  const window = (end: number) => {
    const quarters = [end - 3, end - 2, end - 1, end].map((q) =>
      byQuarter.get(q)
    )
    if (quarters.some((q) => !q)) return null
    const present = quarters as Array<{
      value: AiHandoffFigure
      weight: number
    }>
    const weight = present.reduce((sum, q) => sum + q.weight, 0)
    const value = Math.round(
      present.reduce((sum, q) => sum + (q.value.value ?? 0) * q.weight, 0) /
        weight
    )
    return { end, value, weight, unit: present[0].value.unit }
  }
  const fig = (
    value: number,
    unit: AiHandoffFigure['unit']
  ): AiHandoffFigure => ({ value, unit, source: field.source, as_of: asOf })
  const rowFor = (
    name: string,
    w: NonNullable<ReturnType<typeof window>>,
    latest?: number
  ): AiHandoffRow => ({
    window: name,
    from: label(w.end - 3),
    to: label(w.end),
    [trend.value_key]: fig(w.value, w.unit),
    [trend.weight_key]: fig(w.weight, 'count'),
    ...(latest !== undefined
      ? {
          change_to_latest: fig(
            Math.round((latest / w.value - 1) * 1000) / 10,
            'pct'
          ),
        }
      : {}),
  })

  const latest = window(last)
  if (!latest) return []
  const out = [rowFor('last_4_quarters', latest)]
  const yearBefore = window(last - 4)
  if (yearBefore) out.push(rowFor('one_year_earlier', yearBefore, latest.value))
  const fiveYears = window(last - 20)
  if (fiveYears) out.push(rowFor('five_years_earlier', fiveYears, latest.value))
  return out
}

const buildSection = (
  spec: SectionSpec,
  sectionValue: unknown,
  asOf: string
): AiHandoffSection | null => {
  const values: AiHandoffSection['values'] = {}
  for (const field of spec.fields) {
    const rawAt = at(sectionValue, field.path)

    if (field.kind === 'object') {
      // Every primitive leaf of an object, emitted as one record. Used where a
      // section stores dozens of self-describing counts (demographics has 96)
      // that would be absurd to declare one by one, and where the key names
      // carry their own meaning.
      const row: AiHandoffRow = {}
      if (isRow(rawAt)) {
        for (const [k, v] of Object.entries(rawAt)) {
          if (
            v === null ||
            typeof v === 'string' ||
            typeof v === 'number' ||
            typeof v === 'boolean'
          ) {
            row[k] = v
          }
        }
      }
      if (Object.keys(row).length > 0) values[field.key] = [row]
      continue
    }

    if (field.kind === 'series') {
      const rows = seriesRows(rawAt, field, asOf)
      const trend = trendRows(rows, field, asOf)
      if (field.trend && trend.length > 0) values[field.trend.key] = trend
      if (rows.length > 0) values[field.key] = rows
      continue
    }

    if (field.kind === 'list') {
      const list = listValue(rawAt, field.list_path)
      if (list !== undefined) values[field.key] = list
      continue
    }

    const raw =
      field.kind === 'length'
        ? Array.isArray(rawAt)
          ? rawAt.length
          : undefined
        : rawAt
    const v = valueFor(
      raw,
      unitFor(sectionValue, field),
      field.source,
      asOf,
      field.transform
    )
    if (v !== undefined) values[field.key] = v
  }
  // A section with nothing readable is omitted rather than rendered as an empty
  // heading that implies the customer had no data.
  return Object.keys(values).length > 0
    ? {
        id: spec.id,
        title: spec.title,
        ...(spec.provenance ? { provenance: spec.provenance } : {}),
        ...(spec.definitions ? { definitions: spec.definitions } : {}),
        ...(spec.description ? { description: spec.description } : {}),
        values,
      }
    : null
}

/** Section ids that have a declared field map and can therefore be emitted. */
export const SPEC_SECTION_IDS: EstimateSectionId[] = SECTION_SPECS.map(
  (s) => s.id
) as EstimateSectionId[]

/**
 * The chosen sections a spec list could emit but that came out empty: nothing
 * was recorded for them on the share. Sections the payload carries outside
 * `sections` (identity, valuation, overlay) have no spec here and are never
 * listed.
 */
export const emptySections = <T extends AiHandoffSectionId>(
  included: readonly T[],
  sections: readonly AiHandoffSection[],
  specs: readonly SectionSpec[] = SECTION_SPECS
): T[] => {
  const emitted = new Set<AiHandoffSectionId>(sections.map((s) => s.id))
  const emittable = new Set<AiHandoffSectionId>(specs.map((s) => s.id))
  return included.filter((id) => emittable.has(id) && !emitted.has(id))
}

/** The ids a given spec list can emit, in declaration order. */
export const specIds = <T extends AiHandoffSectionId>(
  specs: readonly SectionSpec[]
): T[] => specs.map((s) => s.id) as T[]

/**
 * `specs` defaults to the estimate map so existing callers are unchanged.
 * Land and building pass their own; the extraction machinery itself is
 * artifact-agnostic and must stay that way.
 */
export const buildSections = (
  value: Record<string, unknown>,
  includedSections: readonly AiHandoffSectionId[],
  asOf: string,
  specs: readonly SectionSpec[] = SECTION_SPECS
): AiHandoffSection[] => {
  const out: AiHandoffSection[] = []
  for (const spec of specs) {
    if (!includedSections.includes(spec.id)) continue
    // A spec may read a section stored under a different key than its id —
    // land and building nest their markets under `purchase`/`rent`, and a few
    // specs are reused across artifacts where only the key differs.
    const section = buildSection(
      spec,
      at(value, spec.source_key ?? spec.id),
      asOf
    )
    if (section) out.push(section)
  }
  return out
}
